import { motion } from "framer-motion";
import { useState, useRef } from "react";
import { Play, Pause, Loader2 } from "lucide-react";

const HighlightVideo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      // If not enough buffered, show spinner until it can play through
      if (video.readyState < 3) {
        setIsBuffering(true);
      }
      const playPromise = video.play();
      if (playPromise) {
        playPromise.then(() => setIsPlaying(true)).catch(() => setIsBuffering(false));
      } else {
        setIsPlaying(true);
      }
    }
  };

  return (
    <section className="bg-primary py-20 md:py-28 px-6">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.3em] uppercase text-primary-foreground/60 font-body">
            Watch
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-display font-light text-primary-foreground">
            A Love Otherwise Experience
          </h2>
          <div className="mx-auto mt-6 h-px w-16 bg-champagne/40" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="relative aspect-video w-full max-w-5xl mx-auto overflow-hidden bg-primary/5 cursor-pointer"
          onClick={togglePlay}
        >
          <video
            ref={videoRef}
            src="/videos/love-otherwise-experience.mp4"
            poster="/gallery/love_otherwise-146.jpg"
            className="w-full h-full object-cover"
            playsInline
            preload="metadata"
            onWaiting={() => setIsBuffering(true)}
            onPlaying={() => { setIsBuffering(false); setIsPlaying(true); }}
            onCanPlay={() => setIsBuffering(false)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          />

          {/* Buffering spinner */}
          {isBuffering && (
            <div className="absolute inset-0 flex items-center justify-center bg-foreground/30 pointer-events-none">
              <Loader2 className="w-12 h-12 text-background animate-spin" />
            </div>
          )}

          {/* Play/Pause overlay */}
          {!isBuffering && (
            <div className={`absolute inset-0 flex items-center justify-center bg-foreground/20 transition-opacity duration-300 ${isPlaying ? "opacity-0 hover:opacity-100" : "opacity-100"}`}>
              <div className="w-20 h-20 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-background/90 hover:scale-110 transition-all duration-300">
                {isPlaying ? (
                  <Pause className="w-8 h-8 text-primary" />
                ) : (
                  <Play className="w-8 h-8 text-primary ml-1" />
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default HighlightVideo;
